import { BTW_MULTIPLIER_INCL } from './pdfPricing';

/** @typedef {{ id: string, name: string, drawings: object[], savedAt?: string }} QuoteSection */

export function createQuoteSection(name, drawings = []) {
  const trimmed = String(name || '').trim();
  if (!trimmed || !drawings.length) return null;

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name: trimmed,
    drawings: JSON.parse(JSON.stringify(drawings)),
    savedAt: new Date().toISOString(),
  };
}

export function normalizeQuoteSections(quoteData = {}) {
  const raw = quoteData?.sections;
  if (!Array.isArray(raw)) return [];

  return raw.map((section, sectionIndex) => ({
    id: section.id || `section-${sectionIndex}`,
    name: section.name || '',
    savedAt: section.savedAt || new Date().toISOString(),
    drawings: Array.isArray(section.drawings)
      ? section.drawings.map((drawing, drawingIndex) => ({
          ...drawing,
          id: drawing.id || `drawing-${sectionIndex}-${drawingIndex}`,
        }))
      : [],
  }));
}

/** Build ordered export blocks: saved sections first, then optional current (unsaved) drawings. */
export function buildExportSectionList(quoteSections = [], currentDrawings = [], { currentSectionName = '' } = {}) {
  const blocks = (quoteSections || []).map((section) => ({
    id: section.id,
    name: section.name,
    drawings: section.drawings || [],
  }));

  if (currentDrawings?.length > 0) {
    blocks.push({
      id: 'current-section',
      name: String(currentSectionName || '').trim(),
      drawings: currentDrawings,
      isCurrent: true,
    });
  }

  return blocks;
}

export function isMultiSectionQuote(quoteSections = [], currentDrawings = []) {
  const sectionCount = (quoteSections || []).length;
  if (sectionCount > 1) return true;
  if (sectionCount === 1 && (currentDrawings?.length ?? 0) > 0) return true;
  return false;
}

/** Ex-VAT subtotal from stored unit prices (configurator Summary sidebar). */
export function computeDrawingsStoredTotalExVat(drawings = []) {
  return drawings.reduce((sum, quote) => {
    const qty = quote.config?.quantity ?? 1;
    const unit = quote.pricing?.totalPrice ?? 0;
    return sum + unit * qty;
  }, 0);
}

export function computeDrawingsStoredTotalInclVat(drawings = []) {
  return computeDrawingsStoredTotalExVat(drawings) * BTW_MULTIPLIER_INCL;
}

export function countQuoteDrawings(quoteSections = [], currentDrawings = []) {
  const inSections = (quoteSections || []).reduce(
    (sum, section) => sum + (section.drawings?.length ?? 0),
    0,
  );
  return inSections + (currentDrawings?.length ?? 0);
}

/** All drawings: saved sections first, then current (unsaved) list. */
export function getAllQuoteDrawings(quoteSections = [], currentDrawings = []) {
  return [
    ...(quoteSections || []).flatMap((section) => section.drawings || []),
    ...(currentDrawings || []),
  ];
}
